import { Plus, Archive, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Session {
  id: string;
  title: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

interface ResearchSidebarContentProps {
  sessions: Session[];
  currentSessionId?: string;
  onSelectSession: (sessionId: string) => void;
  onNewSession: () => void;
  onArchiveSession: (sessionId: string) => void;
  isLoading?: boolean;
  pendingSessionId?: string | null;
  isLoadingSession?: boolean;
}

export function ResearchSidebarContent({
  sessions,
  currentSessionId,
  onSelectSession,
  onNewSession,
  onArchiveSession,
  isLoading = false,
  pendingSessionId = null,
  isLoadingSession = false,
}: ResearchSidebarContentProps) {
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Show sessions as soon as the user's query is recorded (message_count >= 1)
  const activeSessions = sessions.filter(s => s.status === 'active' && (s.message_count ?? 0) >= 1);

  return (
    <>
      {/* New Session Button */}
      <div className="p-3">
        <button
          onClick={onNewSession}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 text-text-primary transition-colors"
        >
          <Plus className="w-5 h-5 flex-shrink-0 text-primary" />
          <span className="text-[13px] font-medium">New Research</span>
        </button>
      </div>

      {/* Session List */}
      <div data-tour="session-list" className="flex-1 overflow-y-auto px-3 pb-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-text-muted animate-spin" />
          </div>
        ) : activeSessions.length === 0 ? (
          <div className="text-center py-8 text-text-muted text-sm">
            No research sessions yet.
            <br />
            Start your first inquiry above.
          </div>
        ) : (
          <div className="space-y-1">
            {activeSessions.map((session) => {
              const isThisSessionLoading = pendingSessionId === session.id;
              const isSelected = currentSessionId === session.id;

              return (
                <div
                  key={session.id}
                  className={cn(
                    'group flex items-center rounded-lg transition-colors cursor-pointer border-l-2 border-transparent',
                    isSelected
                      ? 'bg-surface-light/50 border-border-light'
                      : isThisSessionLoading
                        ? 'bg-surface-light/20 animate-pulse'
                        : 'hover:bg-surface-light/30'
                  )}
                >
                  <button
                    onClick={() => onSelectSession(session.id)}
                    disabled={isLoadingSession}
                    className={cn(
                      'flex-1 min-w-0 flex items-center gap-3 px-3 py-2.5 text-left',
                      isLoadingSession && !isThisSessionLoading && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {isThisSessionLoading && (
                      <Loader2 className="w-4 h-4 text-text-muted flex-shrink-0 animate-spin" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-text-primary truncate">
                        {session.title || 'Untitled Research'}
                      </p>
                      <p className="text-xs text-text-muted">
                        {isThisSessionLoading ? 'Loading...' : formatRelativeTime(session.updated_at)}
                      </p>
                    </div>
                  </button>

                  {!isLoadingSession && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchiveSession(session.id);
                      }}
                      className="flex-shrink-0 p-1.5 mr-1 rounded-md hover:bg-surface-light/50 text-text-muted hover:text-error opacity-0 group-hover:opacity-100 transition-all"
                      title="Archive session"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </>
  );
}
